import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Send, Loader2, Zap, CheckCircle2, AlertTriangle, MoreHorizontal, Trash2, Edit3, CalendarClock, Video, Handshake } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAsk } from "@/context/AskContext";
import { ChatEntityCards } from "@/components/ChatEntityCards";
import { ReachOutModal } from "@/pages/Inbox";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// Draft-in-assistant-text detector.
// Two layers, in order:
//   1) STRUCTURAL greeting→signoff — if the reply contains a real email block
//      (Hi/Hey/Hello/Dear/Good <time> …  Best/Thanks/Regards, <name>) we use
//      that. This wins over the header heuristic so trailing summary bullets
//      ("Want me to schedule …") after a proper draft cannot fool the picker.
//   2) HEADER heuristic — fallback for terse assistant replies whose body is
//      short and doesn't look email-shaped, but starts with a draft-intro
//      header like "**Draft message:**", "Suggested reply:", or
//      "**✏️ Draft — Ready to Send**" with a blockquote body underneath.
// Body cleanup: strips a leading markdown blockquote ("> ") prefix from every
// line, and drops leading/trailing "---" dividers. Returns null when nothing
// looks like a draft.
// -----------------------------------------------------------------------------
export function extractAssistantDraft(text) {
  if (!text || typeof text !== "string") return null;

  const stripQuoteAndTrim = (arr) => {
    while (arr.length && /^(\s*|\s*---+\s*)$/.test(arr[0])) arr.shift();
    while (arr.length && /^(\s*|\s*---+\s*)$/.test(arr[arr.length - 1])) arr.pop();
    if (!arr.length) return "";
    const nonBlank = arr.filter((l) => l.trim().length);
    const allQuoted = nonBlank.length > 0 && nonBlank.every((l) => /^\s*>\s?/.test(l));
    const stripped = allQuoted ? arr.map((l) => l.replace(/^\s*>\s?/, "")) : arr;
    return stripped.join("\n").replace(/^["“”']|["“”']$/g, "").trim();
  };

  const lines = text.split("\n");

  // ---- Layer 1 (preferred): structural greeting → signoff ----
  // If the assistant reply contains a real email block (greeting …
  // signoff-name), prefer it over any trailing "here's a summary:" header.
  // This handles the common pattern: **Draft message:** <email> then a
  // bulleted analysis afterwards.
  const greetingRe = /^\s*>*\s*(Hi|Hey|Hello|Dear|Good\s+(?:morning|afternoon|evening))\b/i;
  const fieldRe = /^\s*\**\s*(To|Subject|Cc|Bcc)\s*:/i;
  const signoffRe = /^\s*>*\s*(Best|Thanks|Regards|Cheers|Sincerely|Warm(?:ly|est)?|Talk\s+soon)\b\s*,?\s*$/i;

  let startIdx = -1, signoffIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (startIdx < 0 && (greetingRe.test(lines[i]) || fieldRe.test(lines[i]))) startIdx = i;
    if (startIdx >= 0 && signoffRe.test(lines[i])) { signoffIdx = i; break; }
  }
  if (startIdx >= 0 && signoffIdx > startIdx) {
    const endIdx = Math.min(lines.length - 1, signoffIdx + 1);
    const body = stripQuoteAndTrim(lines.slice(startIdx, endIdx + 1));
    if (body.length >= 20) return body;
  }

  // ---- Layer 2 (fallback): header heuristic ----
  const isHeader = (raw) => {
    const s = raw.replace(/\*\*/g, "").replace(/^\s*[\u2700-\u27BF\uE000-\uF8FF\uD800-\uDBFF\uDC00-\uDFFF\s]+/, "").trim();
    if (!s || s.length > 120) return false;
    const lower = s.toLowerCase();
    const mentionsDraft = /\b(draft|reply|message body|follow[- ]?up|followup|email|note|dm|ping|response)\b/.test(lower);
    if (!mentionsDraft) return false;
    if (/ready|to\s+send|to send|send now/.test(lower)) return true;
    if (/^here'?s?\s+(a|the|what)\s+/.test(lower)) return true;
    if (lower.endsWith(":")) return true;
    return false;
  };
  let headerIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (isHeader(lines[i])) { headerIdx = i; break; }
  }
  if (headerIdx >= 0) {
    const body = stripQuoteAndTrim(lines.slice(headerIdx + 1));
    if (body.length >= 20) return body;
  }

  return null;
}

export default function AskAnything() {
  const [params, setParams] = useSearchParams();
  const { threads, activeThreadId, setActiveThreadId, addMessage, queueMessage, renameThread, deleteThread } = useAsk();
  const threadIdFromUrl = params.get("thread");
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  // People lookup so the "Send now" flow can pre-select a recipient in the
  // ReachOutModal recipient picker. Fetched once on mount.
  const [people, setPeople] = useState([]);
  const [reachOut, setReachOut] = useState(null); // ReachOutModal initial payload
  const endRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    endpoints.people().then((pp) => { if (!cancelled) setPeople(pp); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { setActiveThreadId(threadIdFromUrl || null); }, [threadIdFromUrl, setActiveThreadId]);

  const activeThread = threads.find((t) => t.id === activeThreadId);
  const activeStatus = activeThread?.status;
  const activeMsgCount = activeThread?.message_count;
  useEffect(() => {
    if (!activeThreadId) { setThread(null); setMessages([]); return; }
    let cancelled = false;
    setLoadingThread(true);
    endpoints.ask.getThread(activeThreadId)
      .then((r) => { if (!cancelled) { setThread(r.thread); setMessages(r.messages); } })
      .catch(() => { if (!cancelled) toast.error("Could not load chat"); })
      .finally(() => { if (!cancelled) setLoadingThread(false); });
    return () => { cancelled = true; };
  }, [activeThreadId, activeStatus, activeMsgCount]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true); setInput("");
    try {
      if (!activeThreadId) {
        const t = await queueMessage(text);
        setParams({ thread: t.id });
      } else {
        setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() }]);
        await addMessage(activeThreadId, text);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="h-full flex flex-col">
      {!activeThreadId ? (
        <EmptyLanding input={input} setInput={setInput} sending={sending} onSend={send} />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="border-b border-neutral-200 px-4 md:px-6 py-3 flex items-center gap-3 bg-white">
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-semibold truncate" data-testid="thread-title">{thread?.title || "Loading…"}</h1>
              <ThreadStatusIndicator status={activeThread?.status} step={activeThread?.current_step} />
            </div>
            <ThreadHeaderMenu
              thread={activeThread}
              onRename={() => {
                const t = prompt("Rename chat", activeThread?.title);
                if (t !== null && t.trim()) renameThread(activeThreadId, t.trim());
              }}
              onDelete={async () => {
                if (!confirm("Delete this chat and all its messages?")) return;
                await deleteThread(activeThreadId);
                setParams({});
              }}
              onNew={() => setParams({})}
            />
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:px-8 md:py-6 bg-[#FAFAFA]" data-testid="thread-messages">
            {loadingThread && messages.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 size={14} className="animate-spin" /> Loading conversation…</div>
            )}
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((m) => <Message key={m.id} m={m} onSendNow={(body) => setReachOut({ platform: "email", body, subject: "" })} />)}
              {activeThread?.status === "running" && <RunningIndicator step={activeThread.current_step} />}
              <div ref={endRef} />
            </div>
          </div>

          <div className="border-t border-neutral-200 bg-white p-3 md:p-4">
            <div className="max-w-3xl mx-auto flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={activeThread?.status === "running" ? "Waiting for agent to finish…" : "Reply…"}
                rows={1}
                disabled={activeThread?.status === "running"}
                className="flex-1 text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none resize-none focus:border-neutral-500 disabled:opacity-60"
                data-testid="ask-thread-input"
                style={{ maxHeight: 160 }}
              />
              <button
                onClick={send}
                disabled={sending || !input.trim() || activeThread?.status === "running"}
                className="p-2 rounded-md bg-neutral-900 text-white disabled:bg-neutral-200 disabled:text-neutral-500"
                data-testid="ask-thread-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {reachOut && (
        <ReachOutModal
          initial={reachOut}
          people={people}
          onClose={() => setReachOut(null)}
          onSent={() => setReachOut(null)}
        />
      )}
    </div>
  );
}

function EmptyLanding({ input, setInput, sending, onSend }) {
  const suggestions = [
    { icon: <Sparkles size={12} />, text: "What needs my attention today?" },
    { icon: <Zap size={12} />, text: "Which projects are at risk this week?" },
    { icon: <Edit3 size={12} />, text: "Draft a follow-up to the most silent client" },
    { icon: <CheckCircle2 size={12} />, text: "Show open commitments due this week" },
  ];
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <MeetingPrepCard />
        <div className="text-center mb-8">
          <div className="h-10 w-10 mx-auto rounded-md bg-neutral-900 text-white flex items-center justify-center mb-3">
            <Sparkles size={18} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Ask anything</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Query the workspace, draft messages, or ask me to do it — I can create tasks, projects, escalations and more.
          </p>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm mb-4">
          <textarea
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            placeholder="Ask anything — 'Create a task in Series A Fundraise for the pitch deck review by Friday' works too."
            rows={3}
            className="w-full text-sm bg-transparent outline-none resize-none placeholder-neutral-400"
            data-testid="ask-landing-input"
            style={{ maxHeight: 200 }}
          />
          <div className="flex items-center justify-between pt-2 border-t border-neutral-100 mt-2">
            <span className="text-[10px] text-neutral-400">Enter to send · Shift+Enter for newline</span>
            <button
              onClick={onSend}
              disabled={sending || !input.trim()}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-neutral-900 text-white disabled:opacity-40"
              data-testid="ask-landing-send"
            >
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => setInput(s.text)}
              className="flex items-center gap-2 text-left text-sm px-3 py-2.5 rounded-md border border-neutral-200 bg-white hover:border-neutral-400 hover:bg-neutral-50 transition-colors text-neutral-700"
              data-testid={`ask-suggestion-${i}`}
            >
              <span className="text-neutral-500">{s.icon}</span>
              <span>{s.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MeetingPrepCard — surfaces the next Google Calendar event when it's within
// 15 minutes, with attendees and any open commitments involving them so you
// walk in prepared. Silently no-ops if Google Calendar isn't connected via
// Nango or no meeting is imminent.
// -----------------------------------------------------------------------------
function MeetingPrepCard() {
  const [event, setEvent] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [gc, cc, pp] = await Promise.all([
          endpoints.nango.calendarEvents({ max_results: 10 }).catch(() => ({ events: [] })),
          endpoints.commitments().catch(() => []),
          endpoints.people().catch(() => []),
        ]);
        setCommitments(cc); setPeople(pp);
        const now = Date.now();
        const in15 = now + 15 * 60 * 1000;
        const imminent = (gc.events || [])
          .filter((e) => {
            if (e.all_day) return false;
            const s = e.start_at ? new Date(e.start_at).getTime() : 0;
            return s >= now && s <= in15;
          })
          .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))[0];
        setEvent(imminent || null);
      } catch { /* silent — card just doesn't render */ }
    })();
    // Re-check every 60s so the card appears without a page reload.
    const t = setInterval(() => {
      endpoints.nango.calendarEvents({ max_results: 10 }).then((gc) => {
        const now = Date.now();
        const in15 = now + 15 * 60 * 1000;
        const imminent = (gc.events || [])
          .filter((e) => !e.all_day && e.start_at && new Date(e.start_at).getTime() >= now && new Date(e.start_at).getTime() <= in15)
          .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))[0];
        setEvent(imminent || null);
      }).catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, []);

  const attendeeMatches = useMemo(() => {
    if (!event) return [];
    const emails = new Set((event.attendees || []).map((a) => (a.email || "").toLowerCase()));
    return people.filter((p) => p.email && emails.has(p.email.toLowerCase()));
  }, [event, people]);

  const relatedCommitments = useMemo(() => {
    if (attendeeMatches.length === 0) return [];
    const ids = new Set(attendeeMatches.map((p) => p.id));
    return commitments
      .filter((c) => c.status !== "done" && (ids.has(c.from_person_id) || ids.has(c.to_person_id)))
      .slice(0, 4);
  }, [attendeeMatches, commitments]);

  if (!event) return null;

  const minutesTo = Math.max(0, Math.round((new Date(event.start_at).getTime() - Date.now()) / 60000));

  return (
    <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="meeting-prep-card">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <CalendarClock size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Meeting prep · in {minutesTo} min</span>
          </div>
          <h3 className="text-sm font-semibold text-neutral-900 mt-0.5 truncate">{event.summary}</h3>
          <div className="text-[11px] text-neutral-600 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{new Date(event.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – {new Date(event.end_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            {event.location && <span>· {event.location}</span>}
            {event.hangout_link && (
              <a href={event.hangout_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                <Video size={10} /> Meet
              </a>
            )}
          </div>

          {attendeeMatches.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">Attendees you know</p>
              <div className="flex flex-wrap gap-1.5">
                {attendeeMatches.slice(0, 6).map((p) => (
                  <span key={p.id} className="text-[11px] px-2 py-0.5 rounded-full border border-neutral-200 bg-white text-neutral-700">
                    {p.name}{p.company ? ` · ${p.company}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          {relatedCommitments.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
                <Handshake size={10} /> Open commitments with them
              </p>
              <ul className="space-y-1">
                {relatedCommitments.map((c) => (
                  <li key={c.id} className="text-[12px] text-neutral-800 leading-snug">
                    · {c.what}
                    {c.due_date && <span className="text-neutral-500"> — due {new Date(c.due_date).toLocaleDateString()}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Message({ m, onSendNow }) {
  const isUser = m.role === "user";
  const isTool = m.role === "tool";
  const hasToolCalls = m.role === "assistant" && m.tool_calls?.length;
  // Only surface "Send now" when the assistant's final text actually contains
  // a message-shaped draft. Falls back to null on non-draft answers.
  const draftBody = m.role === "assistant" ? extractAssistantDraft(m.content) : null;

  // Hide raw tool results — the tool-call chips + AI's final answer are enough context.
  if (isTool) return null;

  // Assistant that only produced tool calls (no content yet) → render as condensed chip row.
  if (hasToolCalls && !m.content) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 pl-7 mb-1" data-testid={`msg-toolcall-${m.id}`}>
        {m.tool_calls.map((tc) => (
          <span key={tc.id} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
            <Zap size={10} /> tool · {tc.function.name}
          </span>
        ))}
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end" data-testid={`msg-${m.role}-${m.id}`}>
        <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-neutral-900 text-white text-sm px-4 py-2.5 leading-relaxed">
          <div className="whitespace-pre-wrap">{m.content}</div>
        </div>
      </div>
    );
  }

  // Assistant reply — chip row (if any) + line avatar + white card bubble.
  return (
    <div className="flex flex-col gap-1" data-testid={`msg-${m.role}-${m.id}`}>
      {hasToolCalls && (
        <div className="flex flex-wrap items-center gap-1.5 pl-7">
          {m.tool_calls.map((tc) => (
            <span key={tc.id} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
              <Zap size={10} /> tool · {tc.function.name}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-start gap-2.5">
        <Sparkles size={16} className="text-neutral-500 flex-shrink-0 mt-2" strokeWidth={1.5} />
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white border border-neutral-200 text-neutral-900 text-sm px-4 py-2.5 leading-relaxed">
          <div className="whitespace-pre-wrap">{m.content}</div>
          {m.content && <ChatEntityCards text={m.content} />}
          {draftBody && (
            <div className="mt-3 pt-2.5 border-t border-neutral-100 flex items-center gap-2">
              <button
                onClick={() => onSendNow?.(draftBody)}
                data-testid={`ai-msg-send-now-${m.id}`}
                className="text-xs px-2.5 py-1 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 inline-flex items-center gap-1.5"
              >
                <Send size={11} /> Send now
              </button>
              <span className="text-[11px] text-neutral-500">Pick a platform in the popup — draft is pre-loaded.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RunningIndicator({ step }) {
  return (
    <div className="flex items-start gap-2.5" data-testid="agent-running">
      <Sparkles size={16} className="text-neutral-500 flex-shrink-0 mt-2 animate-pulse" strokeWidth={1.5} />
      <div className="rounded-2xl rounded-tl-sm bg-white border border-neutral-200 text-neutral-600 text-sm px-4 py-2.5 flex items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 animate-pulse" style={{ animationDelay: "0ms" }} />
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 animate-pulse" style={{ animationDelay: "150ms" }} />
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 animate-pulse" style={{ animationDelay: "300ms" }} />
        </div>
        <span>{step || "Thinking…"}</span>
      </div>
    </div>
  );
}

function ThreadStatusIndicator({ status, step }) {
  if (status === "running") return (
    <p className="text-[11px] text-neutral-500 flex items-center gap-1.5"><Loader2 size={10} className="animate-spin" /> {step || "Working…"}</p>
  );
  if (status === "error") return (
    <p className="text-[11px] text-[#FF453A] flex items-center gap-1.5"><AlertTriangle size={10} /> Something went wrong. Send another message to retry.</p>
  );
  return <p className="text-[11px] text-neutral-500">Idle</p>;
}

function ThreadHeaderMenu({ thread, onRename, onDelete, onNew }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  if (!thread) return null;
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="p-1.5 rounded hover:bg-neutral-100" data-testid="thread-menu">
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 rounded-md border border-neutral-200 bg-white shadow-lg py-1 z-30">
          <button onClick={() => { setOpen(false); onNew(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-neutral-100" data-testid="thread-menu-new">
            <Sparkles size={13} /> New chat
          </button>
          <button onClick={() => { setOpen(false); onRename(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-neutral-100" data-testid="thread-menu-rename">
            <Edit3 size={13} /> Rename
          </button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-red-600 hover:bg-neutral-100" data-testid="thread-menu-delete">
            <Trash2 size={13} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}
